/**
 * Add (or replace) the organization's payment card. The card fields are
 * Stripe's own (number, expiry, CVV), so card details go straight to Stripe;
 * the backend only keeps the brand, last 4 digits and expiry. During the
 * trial nothing is charged; after it, the first month (or year) is charged
 * straight away. A first card also picks monthly or annual billing.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { loadStripe, type Stripe } from '@stripe/stripe-js'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { getErrorMessage } from '@/lib/api'
import { quoteTotal } from '@/lib/billing-format'
import { billingApi, type BillingInterval, type BillingStatus, type CardSetup } from '@/services/billing'

interface AddCardDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description?: string
  onAdded?: (status: BillingStatus) => void
}

function IntervalChoice({ value, onChange }: { value: BillingInterval; onChange: (value: BillingInterval) => void }) {
  const statusQuery = useQuery({ queryKey: ['billing-status'], queryFn: billingApi.getStatus, retry: false })
  const price = statusQuery.data?.price
  const options: { value: BillingInterval; label: string; hint: string }[] = [
    { value: 'month', label: 'Monthly', hint: price?.month ? quoteTotal(price.month) : '' },
    { value: 'year', label: `Annual – save ${statusQuery.data?.annual_discount_percent ?? 10}%`, hint: price?.year ? `${quoteTotal(price.year)}, paid up front` : '' },
  ]
  return (
    <RadioGroup value={value} onValueChange={(next) => onChange(next as BillingInterval)} className="gap-2">
      {options.map((option) => (
        <Label
          key={option.value}
          htmlFor={`billing-interval-${option.value}`}
          className="flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 font-normal has-[[data-state=checked]]:border-primary"
        >
          <RadioGroupItem id={`billing-interval-${option.value}`} value={option.value} />
          <span className="flex-1 text-sm font-medium">{option.label}</span>
          {option.hint ? <span className="text-xs text-muted-foreground">{option.hint}</span> : null}
        </Label>
      ))}
      <p className="text-xs text-muted-foreground">For your team today, before tax. Monthly plans can move to annual later.</p>
    </RadioGroup>
  )
}

function CardForm({
  setup,
  chooseInterval,
  onCancel,
  onAdded,
}: {
  setup: CardSetup
  chooseInterval: boolean
  onCancel: () => void
  onAdded: (status: BillingStatus) => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [complete, setComplete] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [interval, setBillingInterval] = useState<BillingInterval>('month')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const card = elements?.getElement(CardElement)
    if (!stripe || !card) return
    setBusy(true)
    setError('')
    const result = await stripe.confirmCardSetup(setup.client_secret, { payment_method: { card } })
    if (result.error) {
      setError(result.error.message || 'Your card could not be verified.')
      setBusy(false)
      return
    }
    try {
      onAdded(await billingApi.cardConfirm(result.setupIntent?.id ?? setup.setup_intent_id, interval))
    } catch (err) {
      setError(getErrorMessage(err))
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      {chooseInterval ? <IntervalChoice value={interval} onChange={setBillingInterval} /> : null}
      <div className="rounded-md border border-input bg-background px-3 py-3">
        <CardElement
          options={{ hidePostalCode: true, style: { base: { fontSize: '15px' } } }}
          onChange={(event) => { setComplete(event.complete); setError(event.error?.message ?? '') }}
        />
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <p className="text-xs text-muted-foreground">Card details go straight to our payment provider, Stripe.</p>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button type="submit" disabled={!stripe || !complete || busy}>
          {busy ? <Spinner className="mr-2" /> : null}
          Save card
        </Button>
      </DialogFooter>
    </form>
  )
}

export function AddCardDialog({
  open,
  onOpenChange,
  title = 'Add a payment card',
  description,
  onAdded,
}: AddCardDialogProps) {
  const queryClient = useQueryClient()
  const statusQuery = useQuery({ queryKey: ['billing-status'], queryFn: billingApi.getStatus, retry: false, enabled: open })
  // A first card starts the subscription, so it picks monthly or annual.
  const chooseInterval = !statusQuery.data?.card
  const inTrial = statusQuery.data?.plan === 'trial'
  const defaultDescription = inTrial
    ? 'You won’t be charged during your free trial. When it ends you pay your plan’s rates for your Admin / Manager users and Field Workers; your team size sets the plan.'
    : 'Your first payment is taken now, at your plan’s rates for your Admin / Manager users and Field Workers.'

  const [setup, setSetup] = useState<CardSetup | null>(null)
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    if (!open) {
      setSetup(null)
      setLoadError('')
      return
    }
    let cancelled = false
    billingApi
      .cardSetup()
      .then((data) => {
        if (cancelled) return
        setSetup(data)
        setStripePromise(loadStripe(data.publishable_key))
      })
      .catch((err) => { if (!cancelled) setLoadError(getErrorMessage(err)) })
    return () => { cancelled = true }
  }, [open])

  const handleAdded = (status: BillingStatus) => {
    queryClient.setQueryData(['billing-status'], status)
    void queryClient.invalidateQueries({ queryKey: ['billing-status'] })
    if (status.card?.payment_failed) {
      toast.warning(`Card ending ${status.card.last4} saved, but the bank declined the outstanding payment. Try another card or contact your bank.`)
    } else {
      toast.success(status.card ? `Card ending ${status.card.last4} saved` : 'Card saved')
    }
    onOpenChange(false)
    onAdded?.(status)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description ?? defaultDescription}</DialogDescription>
        </DialogHeader>
        {loadError ? <p className="text-sm text-destructive">{loadError}</p> : null}
        {!loadError && (!setup || !stripePromise) ? <div className="flex justify-center py-6"><Spinner /></div> : null}
        {setup && stripePromise ? (
          <Elements stripe={stripePromise}>
            <CardForm setup={setup} chooseInterval={chooseInterval} onCancel={() => onOpenChange(false)} onAdded={handleAdded} />
          </Elements>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
